import { useAppDispatch } from "@/store/hooks";
import { commentOnLeadTransactions, meetingAnalysis } from '@/store/slices/customerSlice';
import {
  Avatar,
  Paper,
  Typography,
  Checkbox,
  IconButton,
  Button,
  TextField,
  Tooltip,
  Chip,
  CircularProgress,
} from "@mui/material";
import { FaPaperclip, FaPaperPlane } from "react-icons/fa";
import { useEffect, useState, useRef, memo, lazy, useCallback, Suspense, useMemo } from "react";
import type { RootState } from "@/store/rootReducer";
import { useSelector } from "react-redux";
import { dateFormatter } from "@/components/DateFormat/DateFormat";
import ViewLocation from "@/components/ViewLocation/ViewLocation";
import { MODAL_ID } from "@/lib/constants";
import { setModalOpen } from "@/store/slices/globalSlice";
import toast from "@/lib/toast";
import FilterComponent from "@/pages/FilterComponent/FilterComponent";
import AppliedFilter from "@/pages/FilterComponent/AppliedFilter";
import TimelineObjectiveModal from "@/pages/Dashboard/Dashboard360/ObjectiveMpdal/ObjectiveModal";
import StatusModal from "@/pages/Dashboard/Dashboard360/StatusModal/StatusModal";
import { getLeadTransaction, getVisitData } from "@/store/slices/dealAndPipelineSlice";
import { useDebounce } from "@/hooks/debounce";
import { getOpportunityCustomFields } from "@/store/slices/opportunityConfigurationSlice";
import { pushFile, type AttachmentItem } from "@/lib/methodUtils";
import { getLeadImage } from "@/store/slices/teamSlice";
import PreviewDialog from "@/components/PreviewDialog/PreviewDialog";
import { Activity } from "@/lib/constants";
import type { showFilters } from "@/pages/FilterComponent/FilterInterface";

const ViewOpportunity = lazy(() => import("./../../DealAndPipeline/ViewOpportunity/ViewOpportunity"));

interface CustomerTimelineProps {
  statusList: any[];
  visitPurposeList: any[];
  leadId: number;
  opportunityId?: any;
  opportunityData?: any;
  oppNumber: number;
  type: string;
  setSummaryText: React.Dispatch<React.SetStateAction<string>>;
}

const PAGE_SIZE = 50;

const CustomerTimeline: React.FC<CustomerTimelineProps> = ({ statusList, visitPurposeList, leadId, opportunityId, opportunityData, oppNumber, type, setSummaryText }) => {
  const dispatch = useAppDispatch();
  const containerRef = useRef<HTMLDivElement>(null);
  const isFetchingRef = useRef(false);
  const isFirstRender = useRef(true);
  const latestRequestIdRef = useRef(0);

  const { modalOpen } = useSelector((state: RootState) => state.global);

  const [items, setItems] = useState<any[]>([]);
  const [from, setFrom] = useState(1);
  const [to, setTo] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);

  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce((searchText || "").trim());

  const [showCallInputs, setShowCallInputs] = useState(false);
  const [objectives, setObjectives] = useState<string[]>([]);
  const [note, setNote] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<{ id: number; name: string } | null>(null);

  const [location, setLocation] = useState({ lat: "", lng: "" });
  const [filterData, setFilterData] = useState<any>({ statusIds: [], byLeadStatus: null });
  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
  const [isFilterApplied, setIsFilterApplied] = useState<boolean>(false);
  const [invalidComment, setInvalidComment] = useState<boolean>(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState<any>(null);
  const [statusError, setStatusError] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [attachments, setAttachments] = useState<AttachmentItem[]>([]);

  const { opportunityCustomFields } = useSelector((state: RootState) => state.opportunityConfiguration);

  const isFormValid = useMemo(() => {
    return showCallInputs
      ? note.trim().length > 0 &&
      selectedStatus !== null
      : note.trim().length > 0;
  }, [showCallInputs, note, selectedStatus]);

  // fetch function
  const fetchTransactions = useCallback(
    async (currentFrom: number, currentTo: number, append = false) => {
      if (!leadId) return;

      const requestId = ++latestRequestIdRef.current;
      setLoading(true);
      const container = containerRef.current;
      const previousScrollHeight = container?.scrollHeight || 0;

      try {
        const res: any = await dispatch(
          getLeadTransaction({
            From: currentFrom,
            To: currentTo,
            FromDate: null,
            ToDate: null,
            IsCheckIn: "",
            IsProductsAvailable: "",
            IsVisitForm: "",
            LeadId: leadId,
            OpportunityId: opportunityId || "",
            Text: debouncedSearch,
            StatusIds: (filterData.statusIds || [])
              .map((s: any) => s.StatusId)
              .join(","),
            ActivityType: filterData?.byLeadStatus?.StatusId,
          })
        ).unwrap();

        if (requestId !== latestRequestIdRef.current) return;

        setSummaryText(res?.Summary || "");

        const apiItems = res.Items || [];
        setTotalRecords(res.TotalRecords || 0);

        const reversedItems = [...apiItems].reverse();

        if (append) {
          setItems((prev) => [...reversedItems, ...prev]);

          requestAnimationFrame(() => {
            if (containerRef.current) {
              const newHeight = containerRef.current.scrollHeight;
              containerRef.current.scrollTop = newHeight - previousScrollHeight;
            }
          });
        } else {
          setItems(reversedItems);

          requestAnimationFrame(() => {
            if (containerRef.current) {
              containerRef.current.scrollTop = containerRef.current.scrollHeight;
            }
          });
        }
      } catch (error) {
        if (requestId === latestRequestIdRef.current) {
          toast.error("Failed to fetch transactions");
        }
      } finally {
        if (requestId === latestRequestIdRef.current) {
          setLoading(false);
          isFetchingRef.current = false;
        }
      }
    },
    [leadId, opportunityId, debouncedSearch, filterData, dispatch, setSummaryText]
  );

  // initial load
  useEffect(() => {
    if (!leadId) return;

    latestRequestIdRef.current += 1;
    setItems([]);
    setFrom(1);
    setTo(PAGE_SIZE);
    void fetchTransactions(1, PAGE_SIZE, false);
  }, [leadId]);

  // search + filter change
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    latestRequestIdRef.current += 1;
    setFrom(1);
    setTo(PAGE_SIZE);
    setItems([]);
    void fetchTransactions(1, PAGE_SIZE, false);
  }, [debouncedSearch, filterData]);

  useEffect(() => {
    setIsFilterApplied(
      (filterData?.statusIds?.length ?? 0) > 0 ||
      !!filterData?.byLeadStatus
    );
  }, [filterData]);

  useEffect(() => {
    dispatch(getOpportunityCustomFields());
  }, [dispatch]);

  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container || loading || isFetchingRef.current) return;

    if (container.scrollTop === 0) {
      if (to >= totalRecords) return;

      isFetchingRef.current = true;

      const newFrom = from + PAGE_SIZE;
      const newTo = to + PAGE_SIZE;

      setFrom(newFrom);
      setTo(newTo);

      fetchTransactions(newFrom, newTo, true);
    }
  }, [
    loading,
    from,
    to,
    totalRecords,
    fetchTransactions
  ]);

  const handleSend = useCallback(() => {
    if (!isFormValid) return;

    setIsSubmitted(true);
    let hasError = false;

    if (!note.trim()) {
      setInvalidComment(true);
      hasError = true;
    } else {
      setInvalidComment(false);
    }

    if (showCallInputs && !selectedStatus) {
      setStatusError(true);
      toast.error("Status is required");
      hasError = true;
    } else {
      setStatusError(false);
    }

    if (hasError) {
      setIsSubmitted(false);
      return;
    }

    let commentNote = "";

    if (type === "Opportunity") {
      if (opportunityData?.OpportunityName && opportunityData?.Tags?.length > 0) {
        commentNote = opportunityData?.OpportunityName + ' :: ' + opportunityData?.Tags.map((tag: any) => tag.TagName).join(', ') + ' :: ' + note;
      } else if (opportunityData?.OpportunityName) {
        commentNote = opportunityData?.OpportunityName + ' :: ' + note;
      }
    } else {
      commentNote = note || "";
    }

    const action = showCallInputs
      ? meetingAnalysis({
        _isEditMode: false,
        _userId: 0,
        StatusId: selectedStatus?.id,
        VisitPurposeIds: objectives,
        Note: commentNote,
        LeadId: leadId,
        OpportunityId: opportunityId,
        IsCallLog: true,
      })
      : commentOnLeadTransactions({ LeadId: leadId, Note: commentNote, OpportunityId: opportunityId });

    dispatch(action)
      .unwrap()
      .then(() => {
        toast.success("Notes added successfully");
        setNote("");
        setObjectives([]);
        setSelectedStatus(null);
        setFrom(1);
        setTo(PAGE_SIZE);
        fetchTransactions(1, PAGE_SIZE, false);
      })
      .catch(() => {
        toast.error("Failed to submit");
      })
      .finally(() => {
        setIsSubmitted(false);
      });
  }, [
    isFormValid,
    note,
    showCallInputs,
    selectedStatus,
    objectives,
    leadId,
    opportunityId,
    dispatch,
    type,
    opportunityData,
    fetchTransactions,
  ]);

  const objectiveOptions = useMemo(() => {
    return (
      visitPurposeList?.map((o: any) => ({
        value: o.Id,
        label: o.VisitPurpose,
      })) || []
    );
  }, [visitPurposeList]);

  const viewLocation = useCallback((lat: string, lng: string) => {
    setLocation({ lat, lng });
    dispatch(setModalOpen({ id: MODAL_ID.VIEW_LOCATION, data: null }));
  }, [dispatch]);

  const getStatusBadge = useCallback((status: string) => {
    const map: any = {
      "hot": { color: "#ff9267", text: "H" },
      "cold": { color: "#67d8ff", text: "C" },
      "warm": { color: "#ffbf67", text: "W" },
      "success": { color: "#bedf9d", text: "S" },
      "not interested": { color: "#c294fc", text: "N" },
    };
    return map[status?.toLowerCase()] || {};
  }, []);

  const viewOpportunity = useCallback((opportunityId: number) => {
    dispatch(getVisitData(opportunityId))
      .unwrap()
      .then((opportunity) => {
        setSelectedOpportunity({ opportunity, oppNumber });
        dispatch(setModalOpen({ id: "ViewOpportunity", data: opportunity }));
      });
  }, [dispatch, oppNumber]);

  const closeModalHandler = useCallback(() => {
    dispatch(setModalOpen({ id: null, data: null }));
    setSelectedOpportunity(null);
  }, [dispatch]);

  const dynamicHeight = isFilterApplied ? "58vh" : "63vh";

  const handleFilterClick = (e: React.MouseEvent<HTMLElement>) => {
    setFilterAnchor((prev) =>
      prev ? null : e.currentTarget
    );
  };

  const viewImage = useCallback(async (leadTransactionId: number) => {
    const res = await dispatch(getLeadImage(leadTransactionId)).unwrap();

    const list: AttachmentItem[] = [];

    if (res?.data?.length > 0) {
      res?.data?.forEach((file: any) => {
        pushFile(file, list, "blob");
      });
    }

    setAttachments(list);
    dispatch(setModalOpen({ id: "PreviewImage", data: list }));
  }, [dispatch]);

  const showFilters: showFilters = {
    status: { show: true, mode: "multi" },
    byLeadStatus: { show: true, mode: "single" }
  };

  return (
    <div className="px-6">
      <div className="mb-8">
        {/* header */}
        <div className="flex justify-between items-center border-b border-solid border-gray-200 pb-4 mb-4">
          <Tooltip title="Filter">
            <Button variant="contained" onClick={handleFilterClick}>
              Filter
            </Button>
          </Tooltip>

          <FilterComponent
            anchorEl={filterAnchor}
            onClose={() => setFilterAnchor(null)}
            onApply={setFilterData}
            appliedFilter={filterData}
            list={{ statusList: statusList, leadStatusList: Activity }}
            showFilters={showFilters}
            filterName="timeline"
            width="400px"
          />

          <TextField
            placeholder="Search..."
            variant="standard"
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>

        {/* applied filter view */}
        <AppliedFilter filterData={filterData} onClearFilter={setFilterData} showFilters={showFilters} filterName="timeline" />
      </div>

      {/* Timeline */}
      <div ref={containerRef} onScroll={handleScroll} className="overflow-y-auto transition-all duration-300" style={{ height: dynamicHeight }}>
        <div className="flex flex-col gap-2">
          {loading && (
            <Typography align="center">Loading...</Typography>
          )}

          {!loading && items.length === 0 && (
            <Typography align="center" color="text.secondary" sx={{ mt: 2 }}>
              No records found
            </Typography>
          )}

          {items.map((item, index) => {
            const { color, text } = getStatusBadge(item?.Status);

            const objectiveArr: string[] = [];
            if (item.Objective) {
              item.Objective.split(",").forEach((obj: string) => {
                objectiveArr.push(obj.trim());
              });
            }

            return (
              <Paper key={item?.Id ?? `${item?.CreatedDate ?? "timeline"}-${index}`} sx={{ p: 2, border: "1px solid #C6D1D7", boxShadow: "5px 3px 5px #c6d1d780" }}>
                <div className={`text-[14px] ${item?.OpportunityId ? "cursor-pointer" : "cursor-default"}`} onClick={() => item?.OpportunityId ? viewOpportunity(item?.OpportunityId) : null}>{item?.Note}</div>

                {item?.Objective && (
                  <div className="flex gap-2 items-center">
                    <Typography fontWeight={600} fontSize={12}>
                      Objective:
                    </Typography>

                    <div className="flex flex-wrap">
                      {objectiveArr && objectiveArr.map((obj, idx) => (
                        <Chip key={`${obj}-${idx}`} label={obj} sx={{ margin: "0 5px", borderRadius: "5px", fontWeight: 600, fontSize: 10, height: 22 }} />
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-between">
                  <Typography fontSize={12} color="text.secondary">
                    {item?.UserName} on {dateFormatter(item?.CreatedDate, 'dd MMM yyyy')} at {dateFormatter(item?.CreatedDate, 'hh:mm a')}
                  </Typography>

                  <div className="flex items-center gap-2">
                    {item?.IsCallLog && (
                      <img src="../../assets/svg/call.svg" width={16} />
                    )}

                    {item?.Latitude !== 0 && item?.Longitude !== 0 && (
                      <img src="../../assets/svg/locate.svg"
                        width={16}
                        className="cursor-pointer"
                        onClick={() => viewLocation(item.Latitude, item.Longitude)} />
                    )}

                    {item?.HasImages && (
                      <img
                        src="./assets/media/attachment.svg"
                        width="16"
                        alt="attachment"
                        onClick={() => viewImage(item?.Id)}
                        className="cursor-pointer"
                      />
                    )}

                    {text && (
                      <Avatar
                        sx={{
                          bgcolor: color,
                          width: 20,
                          height: 20,
                          fontSize: 10,
                        }}
                      >
                        {text}
                      </Avatar>
                    )}
                  </div>
                </div>
              </Paper>
            );
          })}
        </div>
      </div>

      {/* note input */}
      <div className="flex items-center gap-4 w-full mt-4">
        <Checkbox
          checked={showCallInputs}
          onChange={(e) => setShowCallInputs(e.target.checked)}
          sx={{ padding: "0px" }}
        />

        <span className="whitespace-nowrap">Is Call Log</span>

        {showCallInputs && (
          <TextField
            placeholder="Status *"
            variant="standard"
            value={selectedStatus?.name || ""}
            onClick={() =>
              dispatch(setModalOpen({ id: MODAL_ID.STATUS_LIST, data: null }))
            }
            sx={{ minWidth: 140 }}
            required
            error={statusError}
            helperText={statusError ? "Status is required" : ""}
          />
        )}

        {showCallInputs && (
          <TextField
            placeholder="Objectives"
            variant="standard"
            value={
              objectives
                .map(obj =>
                  objectiveOptions.find((o: any) => o.value === obj)?.label
                )
                .join(", ") || ""
            }
            onClick={() =>
              dispatch(setModalOpen({ id: MODAL_ID.TIMELINE_MODAL, data: null }))
            }
            sx={{ minWidth: 180 }}
          />
        )}

        <TextField
          placeholder="Notes *"
          variant="standard"
          value={note}
          onChange={(e) => {
            setNote(e.target.value);
            if (e.target.value.trim()) setInvalidComment(false);
          }}
          fullWidth
          required
          error={invalidComment}
          helperText={invalidComment ? "Note is required" : ""}
        />

        {/* Send Button */}
        {isSubmitted === false && <IconButton color="primary" onClick={handleSend}>
          <FaPaperPlane size={14} color={isFormValid === true ? "#5867dd" : "#74788d"} />
        </IconButton>}
        {isSubmitted === true && <IconButton><CircularProgress size={15} sx={{ color: "#74788d" }} /></IconButton>}
      </div>

      <ViewLocation
        title="Location"
        width="800px"
        modalOpen={modalOpen.id === MODAL_ID.VIEW_LOCATION}
        closeModal={() => dispatch(setModalOpen({ id: null, data: null }))}
        lat={location.lat}
        lng={location.lng}
      />

      <StatusModal
        open={modalOpen.id === MODAL_ID.STATUS_LIST}
        onClose={() => dispatch(setModalOpen({ id: null, data: null }))}
        onSelect={(value: any) => {
          setSelectedStatus(value);
          dispatch(setModalOpen({ id: null, data: null }));
        }}
      />

      <TimelineObjectiveModal
        open={modalOpen.id === MODAL_ID.TIMELINE_MODAL}
        objectiveOptions={objectiveOptions}
        initialSelected={objectives}
        onSave={setObjectives}
      />

      <Suspense fallback={null}>
        <ViewOpportunity
          item={selectedOpportunity}
          viewModalOpen={modalOpen.id === "ViewOpportunity"}
          closeModal={closeModalHandler}
          opportunityCustomFields={opportunityCustomFields}
        />
      </Suspense>

      <PreviewDialog
        open={modalOpen.id === "PreviewImage"}
        onClose={() => dispatch(setModalOpen({ id: null, data: null }))}
        fileUrl={attachments?.map((item) => item.url)}
        fileName={attachments?.map((item) => item.name)}
        fileType="image"
      />
    </div>
  );
}

export default memo(CustomerTimeline);
